<template lang="pug">
div(class="main-container")
    input(ref="photoInput" type="file" accept="image/*" style="display:none" @change="onPhotoSelected")

    //- ── Photo modal ─────────────────────────────────────────────────
    b-modal(v-model="showPhotoModal" centered hide-footer title="Profile Photo" body-class="photo-modal-body")
        div(class="d-flex flex-column align-items-center" style="gap: 1rem;")
            div(class="photo-modal-img-wrap")
                b-img(
                    v-if="clientPic"
                    :src="`data:image/png;base64,${clientPic}`"
                    rounded="circle"
                    fluid
                    class="photo-modal-img"
                    alt="Client photo"
                )
                div(v-else class="photo-modal-placeholder d-flex align-items-center justify-content-center")
                    b-icon(icon="person" font-scale="4" variant="secondary")
            b-button(
                variant="outline-secondary"
                class="w-100 d-flex align-items-center justify-content-center"
                style="gap: 0.5rem;"
                :disabled="uploadingPhoto"
                @click="triggerPhotoUpload"
            )
                b-spinner(v-if="uploadingPhoto" small class="mr-2")
                b-icon(v-else icon="pencil" class="mr-2")
                | Change Photo

    //- ── Header ──────────────────────────────────────────────────────
    div(class="client-header d-flex align-items-center p-3" style="gap: 1rem;")
        div(class="avatar-wrapper" @click="showPhotoModal = true")
            b-avatar(
                :src="clientPic ? `data:image/png;base64,${clientPic}` : null"
                size="72px"
                @error.native="clientPicFallback = true"
            )
            div(class="avatar-edit-badge d-flex align-items-center justify-content-center")
                b-icon(icon="pencil-fill" class="pencil-icon")
        div
            h4(class="m-0 client-name") {{ clientName || 'Client' }}
            div(class="d-flex align-items-center mt-1" style="gap:0.5rem;")
                b-badge(
                    v-if="clientStatus"
                    :variant="statusVariant"
                    class="status-badge"
                ) {{ clientStatus }}
                span(v-if="clientNric" class="nric-text") {{ clientNric }}

    //- ── Info cards ──────────────────────────────────────────────────
    div(class="info-section px-3 mt-3 d-flex flex-column" style="gap: 0.75rem;")

        div(v-if="clientGender" class="info-card p-3")
            div(class="d-flex align-items-center" style="gap:0.5rem;")
                b-icon(icon="person" class="info-icon")
                span(class="info-label") Client Gender
            p(class="m-0 mt-1 cg-name") {{ clientGender }}

        div(v-if="clientAge && clientDob" class="info-card p-3")
            div(class="d-flex align-items-center" style="gap:0.5rem;")
                b-icon(icon="calendar" class="info-icon")
                span(class="info-label") Client Age
            p(class="m-0 mt-1 cg-name") {{ clientDob }} ({{ clientAge }} years)

        //- Caregivers card
        div(v-if="profileLoading" class="info-card p-3")
            b-skeleton(width="50%" height="1rem" class="mb-2")
            b-skeleton(width="80%" height="1rem")

        div(v-else-if="caregivers.length > 0" class="info-card p-3")
            div(class="d-flex align-items-center mb-2" style="gap:0.5rem;")
                b-icon(icon="people" class="info-icon")
                span(class="info-label") Linked Caregivers
            div(class="d-flex flex-column" style="gap:0.25rem;")
                p(
                    v-for="(name, i) in caregivers"
                    :key="i"
                    class="m-0 cg-name"
                ) {{ name }}

        //- CARA membership (always shown, even after "Not Interested")
        cara-membership(
            layout="profile"
            :client-id="clientId"
            :caregiver-id="caregiverId"
        )

    hr(class="mx-3 mt-3")

    //- ── Neeurofit reports ───────────────────────────────────────────
    neeurofit-reports(v-if="clientId" :clientId="clientId")
</template>

<script>
import NeeurofitReports from '@/components/NeeurofitReports.vue';
import CaraMembership from '@/components/CaraMembership.vue';
import { mapState } from 'vuex';

export default {
    components: { NeeurofitReports, CaraMembership },
    data() {
        return {
            profileLoading: false,
            clientStatus: null,
            clientNric: null,
            clientGender: null,
            clientAge: null,
            clientDob: null,
            clientPicFallback: false,
            caregivers: [],
            uploadingPhoto: false,
            showPhotoModal: false,
        };
    },
    computed: {
        ...mapState({
            clientId: 'cli_id',
            clientName: 'cli_name',
        }),
        caregiverId() {
            return this.$root.currentUser?.[0]?.crb5c_fow_caregiverid ?? null;
        },
        clientPic() {
            // cli_pic is set only after changeState; fall back to the users array
            // (same source the navbar header uses) so the photo shows on first load too
            return this.$store.state.cli_pic ||
                this.$store.state.users?.find(u => u.id === this.clientId)?.photo ||
                null;
        },
        statusVariant() {
            if (!this.clientStatus) return 'secondary';
            const s = this.clientStatus.toLowerCase();
            if (s.includes('active')) return 'success';
            if (s.includes('discharge')) return 'danger';
            return 'secondary';
        },
    },
    watch: {
        clientId: {
            handler(val) {
                if (val) this.fetchProfile(val);
            },
            immediate: true,
        },
    },
    methods: {
        async fetchProfile(clientId) {
            this.profileLoading = true;
            this.caregivers = [];
            try {
                const { data } = await this.$root.axios.get(`/client-profile/${clientId}`);
                this.clientStatus = data?.status ?? null;
                this.clientNric = data?.nric ?? null;
                this.clientGender = data?.gender ?? null;
                this.clientAge = data?.age ?? null;
                this.clientDob = data?.dob ? new Date(data.dob).toLocaleDateString() : null;
                this.caregivers = Array.isArray(data?.caregivers) ? data.caregivers : [];
            } catch (err) {
                console.error('[ClientPage] fetchProfile error:', err?.response?.data || err.message || err);
            } finally {
                this.profileLoading = false;
            }
        },
        triggerPhotoUpload() {
            this.$refs.photoInput.click();
        },
        onPhotoModalClose() {
            this.showPhotoModal = false;
        },
        onPhotoSelected(event) {
            const file = event.target.files[0];
            if (!file) return;
            event.target.value = '';
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const MAX = 400;
                    let { width, height } = img;
                    if (width > MAX || height > MAX) {
                        if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
                        else { width = Math.round(width * MAX / height); height = MAX; }
                    }
                    canvas.width = width;
                    canvas.height = height;
                    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                    this.uploadPhoto(canvas.toDataURL('image/jpeg', 0.8));
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        },
        async uploadPhoto(base64) {
            this.uploadingPhoto = true;
            try {
                await this.$root.axios.patch(`/client-profile/${this.clientId}/photo`, { photoBase64: base64 });
                const raw = base64.replace(/^data:image\/\w+;base64,/, '');
                this.$store.commit('setCliPic', { photo: raw, userId: this.clientId });
                this.showPhotoModal = false;
                this.$bvToast.toast('Profile photo updated!', { title: 'Success', variant: 'success', solid: true });
            } catch (err) {
                console.error('[ClientPage] uploadPhoto error:', err);
                this.$bvToast.toast('Failed to update photo. Please try again.', { title: 'Error', variant: 'danger', solid: true });
            } finally {
                this.uploadingPhoto = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    min-height: 100vh;
    background-color: #f8f6fb;
}
.client-header {
    background-color: #fff;
    box-shadow: 0 1px 4px rgba(89, 36, 107, 0.08);
    padding-bottom: 1rem;
}
.client-name {
    font-weight: 700;
    color: #26263e;
}
.status-badge {
    font-size: 0.72rem;
    letter-spacing: 0.02em;
}
.nric-text {
    font-size: 0.75rem;
    color: #8967b3;
    font-weight: 500;
}
.info-section {
    // container for info cards
}
.info-card {
    background: #fff;
    border: 1px solid #e8dff4;
    border-radius: 10px;
    box-shadow: 0 1px 4px rgba(89, 36, 107, 0.06);
}
.info-icon {
    color: #8967b3;
    font-size: 1rem;
}
.info-label {
    font-size: 0.85rem;
    color: #666;
    font-weight: 500;
}
.cg-name {
    font-size: 0.88rem;
    font-weight: 500;
    color: #26263e;
}
.avatar-wrapper {
    position: relative;
    cursor: pointer;
    width: 72px;
    height: 72px;
    flex-shrink: 0;

    .avatar-edit-badge {
        position: absolute;
        bottom: 0;
        right: 0;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: #8967b3;
        border: 2px solid #fff;
        box-shadow: 0 1px 4px rgba(0,0,0,0.18);

        .pencil-icon {
            color: #fff;
            font-size: 0.6rem;
        }
    }
}
.photo-modal-img-wrap {
    width: 180px;
    height: 180px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid #d8c4ec;
    background: #f8f6fb;
    display: flex;
    align-items: center;
    justify-content: center;

    .photo-modal-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
    .photo-modal-placeholder {
        width: 100%;
        height: 100%;
    }
}
</style>